import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  CopyTemplateItemDocument,
  GetTemplateItemsDocument,
  TemplateItemDataFragment,
} from '../../../../data/types/api/graphql';
import { FormError } from '../../../../data/utils/form.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type TemplateCopyPromptProps = {
  templateItem: TemplateItemDataFragment;
  onCopied?: (value: TemplateItemDataFragment) => void;
  onClose?: () => void;
};

export function TemplateCopyPrompt({
  templateItem,
  onCopied,
  onClose,
}: TemplateCopyPromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [createTemplateItem] = useMutation(CopyTemplateItemDocument, {
    refetchQueries: [GetTemplateItemsDocument],
  });

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await createTemplateItem({
        variables: {
          id: templateItem?.id ?? '',
          copyText: intl.formatMessage({
            id: 'template.template-copy.copy',
          }),
        },
      });

      if (result.data?.templateCopy.template) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'template.template-copy.success',
          }),
        });

        onCopied?.(templateItem);

        onClose?.();
      } else {
        (result.data?.templateCopy?.errors as FormError[]).forEach(e => {
          showAlertMessage({
            type: AlertMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(e.message)}`,
              defaultMessage: e.message,
            }),
          });
        });
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    templateItem,
    createTemplateItem,
    showAlertMessage,
    onCopied,
    onClose,
  ]);

  if (!templateItem) return null;

  return (
    <ModalPrompt
      testId="template-copy"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="template.template-copy.content" />
      </p>
    </ModalPrompt>
  );
}
